import { type KeyboardEvent, type PointerEvent, type ReactElement, useCallback, useRef, useState } from "react"
import styled from "styled-components"
import { findThumbnailCueForSec, type ThumbnailCue } from "../lib/thumbnailsVtt"
import { clampToDuration, describeProgress } from "../lib/timecode"
import { ScrubPreview } from "./ScrubPreview"

const ARROW_STEP_SECONDS = 5
const PAGE_STEP_SECONDS = 30

/**
 * Generous vertical hit area around a 12px bar. The track is the full width of
 * the frame and the easiest thing on the screen to miss by a few pixels.
 */
const Root = styled.div`
  position: relative;
  padding-block: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  touch-action: none;

  &:focus-visible {
    outline: none;
  }
`

const Track = styled.div`
  position: relative;
  height: ${({ theme }) => theme.player.trackHeight};
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme }) => theme.player.track};
  overflow: hidden;

  ${Root}:focus-visible & {
    outline: 2px solid ${({ theme }) => theme.player.focusRing};
    outline-offset: 3px;
  }
`

const Buffered = styled.div<{ $percent: number }>`
  position: absolute;
  inset-block: 0;
  left: 0;
  width: ${({ $percent }) => `${$percent}%`};
  background-color: rgb(255 255 255 / 0.24);
`

const Played = styled.div<{ $percent: number }>`
  position: absolute;
  inset-block: 0;
  left: 0;
  width: ${({ $percent }) => `${$percent}%`};
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme }) => theme.player.brand};
`

/**
 * Inset by half its own width at both ends so the handle stays inside the track
 * at 0% and 100% instead of hanging off the edge — the same clamp the TV
 * progress bar does with `handleRadiusPercentage`.
 */
const Handle = styled.div<{ $percent: number; $isActive: boolean }>`
  position: absolute;
  top: 50%;
  left: ${({ $percent }) => `${$percent}%`};
  translate: -50% -50%;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme }) => theme.player.brand};
  box-shadow: 0 0 4px 0 rgb(0 0 0 / 0.7);
  scale: ${({ $isActive }) => ($isActive ? 1.15 : 1)};
  transition: scale 120ms ease-out;
`

const HandleTrack = styled.div`
  position: absolute;
  inset-block: 0;
  left: 0.625rem;
  right: 0.625rem;
`

interface PlayerScrubBarProps {
  readonly currentSeconds: number
  readonly durationSeconds: number
  readonly bufferedSeconds: number
  readonly cues: readonly ThumbnailCue[]
  readonly onScrubStart: () => void
  readonly onScrubEnd: (seconds: number) => void
  readonly qaHook: string
}

const percentOf = (seconds: number, durationSeconds: number): number =>
  durationSeconds > 0 ? Math.min(100, Math.max(0, (seconds / durationSeconds) * 100)) : 0

/**
 * The scrub bar, with frame previews.
 *
 * While a drag is in flight the bar shows `dragSeconds`, not the player's
 * `currentSeconds` — the player is still playing the old position and would
 * fight the pointer. It only seeks on release, matching the TV player's
 * `onScrubStart` / `onScrubEnd` pair rather than seeking continuously, which on a
 * long video means one request instead of one per pixel.
 */
export const PlayerScrubBar = ({
  currentSeconds,
  durationSeconds,
  bufferedSeconds,
  cues,
  onScrubStart,
  onScrubEnd,
  qaHook,
}: PlayerScrubBarProps): ReactElement => {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [dragSeconds, setDragSeconds] = useState<number | null>(null)
  const [hoverSeconds, setHoverSeconds] = useState<number | null>(null)

  const secondsAt = useCallback(
    (clientX: number): number => {
      const track = trackRef.current

      if (!track || durationSeconds <= 0) {
        return 0
      }

      const bounds = track.getBoundingClientRect()
      const ratio = bounds.width > 0 ? (clientX - bounds.left) / bounds.width : 0

      return clampToDuration(Math.min(1, Math.max(0, ratio)) * durationSeconds, durationSeconds)
    },
    [durationSeconds],
  )

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (durationSeconds <= 0) {
        return
      }

      event.currentTarget.setPointerCapture(event.pointerId)
      setDragSeconds(secondsAt(event.clientX))
      onScrubStart()
    },
    [durationSeconds, secondsAt, onScrubStart],
  )

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (durationSeconds <= 0) {
        return
      }

      const seconds = secondsAt(event.clientX)

      setHoverSeconds(seconds)
      setDragSeconds(previous => (previous === null ? null : seconds))
    },
    [durationSeconds, secondsAt],
  )

  const finishDrag = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (dragSeconds === null) {
        return
      }

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }

      onScrubEnd(secondsAt(event.clientX))
      setDragSeconds(null)
    },
    [dragSeconds, onScrubEnd, secondsAt],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (durationSeconds <= 0) {
        return
      }

      const seekBy = (delta: number) => {
        event.preventDefault()
        onScrubEnd(clampToDuration(currentSeconds + delta, durationSeconds))
      }

      switch (event.key) {
        case "ArrowLeft":
          return seekBy(-ARROW_STEP_SECONDS)
        case "ArrowRight":
          return seekBy(ARROW_STEP_SECONDS)
        case "PageDown":
          return seekBy(-PAGE_STEP_SECONDS)
        case "PageUp":
          return seekBy(PAGE_STEP_SECONDS)
        case "Home":
          event.preventDefault()
          return onScrubEnd(0)
        case "End":
          event.preventDefault()
          return onScrubEnd(durationSeconds)
        default:
          return undefined
      }
    },
    [currentSeconds, durationSeconds, onScrubEnd],
  )

  const displaySeconds = dragSeconds ?? currentSeconds
  const displayPercent = percentOf(displaySeconds, durationSeconds)
  const previewSeconds = dragSeconds ?? hoverSeconds
  const trackWidth = trackRef.current?.clientWidth ?? 0

  return (
    <Root
      role="slider"
      tabIndex={0}
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.round(durationSeconds)}
      aria-valuenow={Math.round(displaySeconds)}
      aria-valuetext={describeProgress(displaySeconds, durationSeconds)}
      data-qa-hook={qaHook}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onPointerLeave={() => setHoverSeconds(null)}
      onKeyDown={handleKeyDown}
    >
      {previewSeconds !== null && durationSeconds > 0 ? (
        <ScrubPreview
          offsetPx={(percentOf(previewSeconds, durationSeconds) / 100) * trackWidth}
          seconds={previewSeconds}
          cue={findThumbnailCueForSec(cues, previewSeconds)}
          qaHook={`${qaHook}-preview`}
        />
      ) : null}

      <Track ref={trackRef}>
        <Buffered $percent={percentOf(bufferedSeconds, durationSeconds)} />
        <Played $percent={displayPercent} data-qa-hook={`${qaHook}-played`} />
      </Track>

      <HandleTrack>
        <Handle $percent={displayPercent} $isActive={dragSeconds !== null} data-qa-hook={`${qaHook}-handle`} />
      </HandleTrack>
    </Root>
  )
}
